import { Box, useBreakpointValue, usePrefersReducedMotion } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { SidePanelContextType } from '../../contexts/SidePanelContext';
import { SidePanelContext } from '../../contexts/SidePanelContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useMobileDetection } from '../../hooks/ui/useMobileDetection';
import { useCommandPalette } from '../../hooks/ui/useCommandPalette';
import { useInWorkspaceContext } from '../../hooks/workspace/useInWorkspaceContext';
import { CommandPaletteContext } from '../../contexts/CommandPaletteContext';
import { CommandPalette } from '../features/search/CommandPalette';
import { LeftPanelColumn } from './LeftPanelColumn';
import MobileLayout from './MobileLayout';
import SplitViewContainer from './SplitViewContainer';
import { NavRail } from './nav/NavRail';
import { CreateModalHost } from '../features/create/modal/CreateModalHost';
import {
  NAV_RAIL_WIDTH,
  WORKSPACE_SIDEBAR_DEFAULT_WIDTH,
  WORKSPACE_SIDEBAR_MAX_WIDTH,
  WORKSPACE_SIDEBAR_MIN_WIDTH,
  WORKSPACE_SIDEBAR_WIDTH_KEY,
} from './nav/navRailWidth';
import { readPanelWidth } from '../../hooks/ui/usePanelResize';
import { useBirdPanel } from './useBirdPanel';
import { useLeftPanel, type LeftPanel, type LeftPanelSlot } from './useLeftPanel';
import { useSidePanelState } from './useSidePanelState';
import { ROUTES } from '../../constants/routes';

// Re-export useSidePanel for backwards compatibility
export { useSidePanel } from '../../contexts/SidePanelContext';

interface LayoutProps {
  children: ReactNode;
}

interface PanelWidths {
  readonly sidebar: number;
  readonly bird: number;
}

/**
 * How wide the panel column is, given who holds the slot. Each panel reports its
 * own dragged width upward (the container applies it — `usePanelResize`), so the
 * widths are tracked separately and selected here rather than shared: switching
 * panels must not briefly paint the previous one's width.
 */
function panelColumnWidth(panel: LeftPanel, widths: PanelWidths): number {
  if (panel === 'workspace') return widths.sidebar;
  if (panel === 'bird') return widths.bird;
  return 0;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  // Mobile/desktop detection
  const { isMobile, isDesktop: isLargeScreen } = useMobileDetection();

  // Navigation and location
  const navigate = useNavigate();
  const location = useLocation();
  const isOnboarding = location.pathname === '/onboarding';

  // Global command palette (Cmd+K)
  const commandPalette = useCommandPalette();

  // The exclusive left-panel slot: Bower and the workspace sidebar both want
  // the one column right of the rail, so exactly one of them holds it.
  const { workspace } = useWorkspace();
  // The workspace sidebar is shown only while the user is viewing the
  // workspace (the canvas, or an artifact opened from it).
  const inWorkspaceContext = useInWorkspaceContext();
  // Reactive check for narrow desktop windows (below md/768px breakpoint).
  // Must be called before any early returns to satisfy Rules of Hooks.
  const isSmallScreen = useBreakpointValue({ base: true, md: false }) || false;
  const {
    panel,
    open: openLeftPanel,
    close: closeLeftPanel,
    toggle: toggleLeftPanel,
    closeIfBird,
    showWorkspace,
  } = useLeftPanel({
    workspaceId: workspace?.id,
    workspaceContext: inWorkspaceContext,
    narrow: isSmallScreen,
  });

  // Each panel reports its dragged width; this container applies it, both to the
  // panel box and to the page's left margin. Seeded from the SAME stored width
  // the panel will seed itself from, through the same reader — a constant seed
  // meant anyone who had dragged a panel got a first frame at 420 followed by a
  // jump to their own width, in whichever direction. `usePanelResize` reports on
  // mount, but that is a post-paint effect: seeding here means there is no
  // disagreeing frame to correct rather than a shorter one.
  const [sidebarWidth, setSidebarWidth] = useState(() =>
    readPanelWidth(
      WORKSPACE_SIDEBAR_WIDTH_KEY,
      WORKSPACE_SIDEBAR_MIN_WIDTH,
      WORKSPACE_SIDEBAR_MAX_WIDTH,
      WORKSPACE_SIDEBAR_DEFAULT_WIDTH,
    ),
  );
  // One flag, because only one panel is ever mounted to be dragged.
  const [isPanelDragging, setIsPanelDragging] = useState(false);

  const { isSidePanelOpen, sidePanelContent, openSidePanel, handleCloseSidePanel, prevPathname } =
    useSidePanelState({ isLargeScreen });

  // The split view is NOT a slot member: it renders inside the page content, to
  // the RIGHT of the panel column, so it coexists happily with the workspace
  // panel. It cannot coexist with Bower — they would each want ~40% of the
  // viewport — so that single pairwise edge stays explicit, in both directions.
  const togglePanel = useCallback(
    (next: LeftPanelSlot) => {
      if (next === 'bird') handleCloseSidePanel();
      toggleLeftPanel(next);
    },
    [handleCloseSidePanel, toggleLeftPanel],
  );
  const openBird = useCallback(() => {
    handleCloseSidePanel();
    openLeftPanel('bird');
  }, [handleCloseSidePanel, openLeftPanel]);
  // `openSidePanel` is a fresh closure every render (it reads the live content
  // + unsaved-changes state), so it goes through a ref: the WRAPPER stays
  // stable, which is what lets the context value below memoise. Without this,
  // every render — including one per pointermove during a panel drag — minted a
  // new context value and re-rendered all ~22 useSidePanel consumers per frame.
  const openSidePanelRef = useRef(openSidePanel);
  openSidePanelRef.current = openSidePanel;
  const handleOpenSidePanel = useCallback(
    (content: ReactNode) => {
      closeIfBird();
      openSidePanelRef.current(content);
    },
    [closeIfBird],
  );

  const sidePanelContextValue: SidePanelContextType = useMemo(
    () => ({
      isOpen: isSidePanelOpen,
      onOpen: handleOpenSidePanel,
      onClose: handleCloseSidePanel,
      content: sidePanelContent,
    }),
    [isSidePanelOpen, handleOpenSidePanel, handleCloseSidePanel, sidePanelContent],
  );

  // Interaction-triggered motion is gated on the user's motion preference.
  const prefersReducedMotion = usePrefersReducedMotion();

  const isBirdOpen = panel === 'bird';

  const { birdHandover, handleHandoverConsumed, birdPanelWidth, handleBirdWidthChange } =
    useBirdPanel({
      isMobile,
      isLargeScreen,
      isBirdOpen,
      onOpenBird: openBird,
      onCloseBird: closeLeftPanel,
      prevPathname,
    });

  // Return mobile layout for mobile devices
  if (isMobile) {
    // The phone shell gets the same one palette (its Modal is full width
    // below `md`), so the mobile workspace's search opens it.
    return (
      <SidePanelContext.Provider
        value={{
          isOpen: false,
          onOpen: () => {},
          onClose: () => {},
          content: null,
        }}
      >
        <CommandPaletteContext.Provider value={commandPalette.openFromTopBar}>
          <MobileLayout>{children}</MobileLayout>
        </CommandPaletteContext.Provider>
        <CommandPalette
          isOpen={commandPalette.isOpen}
          onClose={commandPalette.onClose}
          initialScope={commandPalette.initialScope}
        />
        <CreateModalHost />
      </SidePanelContext.Provider>
    );
  }

  /*
   * Layout behavior:
   * - Mobile: Full-page routes, no split view
   * - Desktop (lg+): Split view renders attachment beside main content
   * - Desktop (<lg): Callers navigate to full-page route (no split view)
   * - Bower chat open: Split view closes, bird takes priority
   */

  // Split view when: large screen, panel open, bird closed
  const shouldUseSplitView = isLargeScreen && isSidePanelOpen && !isBirdOpen;

  const columnWidth = panelColumnWidth(panel, {
    sidebar: sidebarWidth,
    bird: birdPanelWidth,
  });

  // Content margin — the rail always occupies its width (`NAV_RAIL_WIDTH`), and on a wide viewport
  // whichever panel holds the slot PUSHES the content across by its own width.
  // On a narrow one the column overlays instead, so the margin stays at the rail
  // width.
  const leftMargin = `${NAV_RAIL_WIDTH + (isSmallScreen ? 0 : columnWidth)}px`;

  // If on onboarding page, return simple layout without sidebar
  if (isOnboarding) {
    return (
      <SidePanelContext.Provider value={sidePanelContextValue}>
        <Box
          minH={{ base: '100dvh', md: '100vh' }}
          maxH={{ base: '100dvh', md: '100vh' }}
          position="relative"
          overflow="hidden"
          bg="bg.canvas"
        >
          <Box w="100%" h={{ base: '100dvh', md: '100vh' }} overflow="auto">
            {children}
          </Box>
        </Box>
      </SidePanelContext.Provider>
    );
  }

  return (
    <SidePanelContext.Provider value={sidePanelContextValue}>
      {/* `data-print` is the hook for the `@media print` block in
          theme/styles.ts. The shell is a fixed-height scroller (every box
          from #root down to the page is 100vh + overflow hidden/auto), so
          Cmd-P printed exactly one screen of a note. Print media flattens
          every `scroller` to height auto and drops every `hide`. Screen
          media never reads the attribute. */}
      <Box
        data-print="scroller"
        minH={{ base: '100dvh', md: '100vh' }}
        maxH={{ base: '100dvh', md: '100vh' }}
        position="relative"
        overflow="hidden"
        // The FRAME, not the canvas. The 2026-09 design makes the shell a
        // parchment ground with a white content panel inset inside it; the
        // rail and the sidebar sit directly on this with no borders.
        bg="frame.bg"
      >
        {/* Fixed nav rail — always mounted, never resizes. Single instance:
            mounting two caused every piece of internal state — onboarding
            panel, import/feedback/invite dialogs, window event listeners — to
            exist twice and fire twice. */}
        <Box
          data-print="hide"
          position="fixed"
          left="0"
          top="0"
          h={{ base: '100dvh', md: '100vh' }}
          w={`${NAV_RAIL_WIDTH}px`}
          zIndex="base"
        >
          <NavRail
            activePanel={panel}
            onTogglePanel={togglePanel}
            onShowWorkspace={showWorkspace}
            inWorkspace={inWorkspaceContext}
          />
        </Box>
        <CreateModalHost />

        {/* The panel column — pushes content on a wide viewport, overlays it on
            a narrow one (where 72 + 352 would leave nothing for the page). */}
        <LeftPanelColumn
          onSearchConversations={() => commandPalette.openWithScope('chats')}
          panel={panel}
          width={columnWidth}
          isOverlay={isSmallScreen}
          onSidebarWidthChange={setSidebarWidth}
          onDragStateChange={setIsPanelDragging}
          onClose={closeLeftPanel}
          birdHandover={birdHandover}
          onBirdHandoverConsumed={handleHandoverConsumed}
          onBirdWidthChange={handleBirdWidthChange}
        />

        {/* Main Content Container — responsive to the panel column */}
        <Box
          data-testid="layout-content"
          data-print="scroller"
          ml={leftMargin}
          // The ease is for open/close/swap, where the page moves by a fixed
          // difference. During a DRAG the margin gets a new target every
          // pointermove, and easing each one makes the content rubber-band
          // ~200ms behind the divider, then reflow for another 200ms after
          // release — so the transition is suspended while a drag is live
          // (and entirely, for users who prefer reduced motion).
          transition={isPanelDragging || prefersReducedMotion ? 'none' : 'margin-left 0.2s ease'}
          minH={{ base: '100dvh', md: '100vh' }}
          maxH={{ base: '100dvh', md: '100vh' }}
          overflow="hidden"
          // TRANSPARENT, not `bg.canvas`. This box spans everything right of
          // the rail, so whatever it paints is what fills the 12px gutter
          // around the curved panel — and painting `bg.canvas` (#F8F7F4) here
          // covers the shell's `frame.bg` (#F0EEE9) everywhere except the
          // 84px rail strip, which silently reduces the parchment frame to a
          // sliver. Light-mode only: both tokens resolve to the same dark
          // canvas, which is why it survived a dark-mode check.
          bg="transparent"
          position="relative"
          display="flex"
          // While a divider drag is live, the page beneath must not hit-test:
          // hover styles re-evaluating every frame as the margin sweeps is
          // wasted work, and pointer capture on the handle owns the gesture.
          // On the PARENT so it covers the split-view branch too — split view
          // coexists with the workspace sidebar by design.
          pointerEvents={isPanelDragging ? 'none' : undefined}
        >
          {/* The curved canvas. ONE wrapper for BOTH branches — split view is
              a sibling of normal mode, and giving the chrome to only one of
              them is how the split view would end up drawn flush on the
              parchment frame with no panel at all.

              The two differ in ONE property: normal mode scrolls the page
              here, while split view owns its own scrolling inside each pane
              and must not add a second scroller around the divider. */}
          <Box
            data-print="scroller"
            flex="1"
            minW={0}
            // 12px inset on three sides; the rail side is already offset by
            // the shell's left margin, so adding it here would double up.
            // Height loses the same 24px so the panel still fits one screen
            // and the frame shows top and bottom.
            // `h` as well as minH/maxH: a percentage height on a CHILD only
            // resolves against a definite height, and SplitViewContainer asks
            // for `h="100%"`. With min/max alone the child would fall back to
            // content height and the panes would collapse.
            h={{ base: '100dvh', md: 'calc(100vh - 24px)' }}
            minH={{ base: '100dvh', md: 'calc(100vh - 24px)' }}
            maxH={{ base: '100dvh', md: 'calc(100vh - 24px)' }}
            m={{ base: 0, md: '12px' }}
            ml={{ base: 0, md: 0 }}
            overflow={shouldUseSplitView ? 'hidden' : 'auto'}
            bg="frame.panel"
            // THE SCROLLBAR SITS INSIDE THE CURVE. A classic scrollbar renders
            // hard against the right edge of its scroll box, so on a `3xl`
            // radius it cut across the corner and read as a strip outside the
            // canvas rather than part of it.
            //
            // The inset is the transparent-border trick: the thumb is painted
            // with `background-clip: content-box` inside a 3px transparent
            // border, so it floats clear of the edge without the track taking
            // any extra width. The track itself is transparent, which is what
            // makes the canvas ground run under it all the way to the corner.
            //
            // Semantic tokens, not the hex values the editor's own scrollbar
            // CSS uses - that file predates the token system and is scoped to
            // one component; this is the app shell and has to follow light and
            // dark. `scrollbarWidth`/`scrollbarColor` cover Firefox, which
            // implements neither the pseudo-elements nor the inset, so it gets
            // a thin quiet bar rather than a wrong one.
            sx={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'var(--chakra-colors-border-default) transparent',
              '&::-webkit-scrollbar': { width: '12px', height: '12px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'border.default',
                borderRadius: 'full',
                border: '3px solid transparent',
                backgroundClip: 'content-box',
              },
              '&::-webkit-scrollbar-thumb:hover': { background: 'border.hover' },
              // The corner where two bars meet, which otherwise paints the UA's
              // default grey square into the rounded corner.
              '&::-webkit-scrollbar-corner': { background: 'transparent' },
            }}
            // Curved canvas. Mobile keeps square corners and no inset: at
            // that width the frame would be a few pixels of trim rather
            // than a visible ground.
            borderRadius={{ base: 0, md: '3xl' }}
            boxShadow={{ base: 'none', md: 'sm' }}
            position="relative"
            pb={{ base: 'env(safe-area-inset-bottom)', md: 0 }}
            // `flex` only for split view, which needs its two panes laid out
            // as flex children. Normal mode stays `block` so the page is a
            // DIRECT child of this box, exactly as before: an extra wrapper
            // here breaks the unbroken `data-print="scroller"` chain that
            // print flattening walks from #root to the page, and the symptom
            // is Cmd-P printing one screen of a long note.
            display={shouldUseSplitView ? 'flex' : 'block'}
          >
            {/* A context provider renders no DOM, so the page stays this box's
                DIRECT child (see the print note above). */}
            <CommandPaletteContext.Provider value={commandPalette.openFromTopBar}>
              {shouldUseSplitView ? (
                <SplitViewContainer
                  leftContent={children}
                  rightContent={sidePanelContent}
                  onClose={handleCloseSidePanel}
                />
              ) : (
                children
              )}
            </CommandPaletteContext.Provider>
          </Box>
        </Box>

        {/* Global Command Palette (Cmd+K) */}
        <CommandPalette
          isOpen={commandPalette.isOpen}
          onClose={commandPalette.onClose}
          initialScope={commandPalette.initialScope}
          // Search results always open the full page. The split view is for
          // comparing a note with its OWN attachment (see useOpenAttachment) —
          // a search hit has no relationship to whatever page is behind it.
          onOpenAttachment={(artifactId) => {
            navigate(ROUTES.attachment(artifactId));
          }}
        />
      </Box>
    </SidePanelContext.Provider>
  );
};

export default Layout;
